<template>
  <div ref="reference" class="relative w-full">
    <Input
      :model-value="displayValue"
      :placeholder="placeholder"
      class="w-full cursor-pointer"
      :variant="variant"
      readonly
      @click="showPicker = true"
    />
    <button
      v-if="hasValue"
      class="absolute right-2 top-1/2 -translate-y-1/2 text-obsidian-400 hover:text-obsidian-600"
      @click.stop="clearRange"
    >
      <XIcon class="h-4 w-4" />
    </button>
    <div v-if="showPicker" ref="pickerContainer" :style="floatingStyles" class="z-[1100]">
      <Datepicker
        v-model="internalDates"
        range
        inline
        week-numbers
        :clearable="false"
        @day-selected="handleRangeSelected"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import Datepicker from '@/components/Datepicker.vue';
import { useDatepickerFloating } from '@/components/datepicker/use-datepicker-floating';
import Input from '@/components/Input.vue';
import { XIcon } from 'lucide-vue-next';
import { getDateFormat } from '@/hooks/use-date-format';
import { formatDate } from '@/hooks/use-formatted-date';

const props = withDefaults(
  defineProps<{
    startDate?: Date | null;
    endDate?: Date | null;
    placeholder?: string;
    autoOpen?: boolean;
    variant?: 'default' | 'ghost';
  }>(),
  {
    startDate: null,
    endDate: null,
    placeholder: '',
    autoOpen: false,
    variant: 'ghost',
  },
);

const emit = defineEmits<{
  (e: 'update:startDate', value: Date | null): void;
  (e: 'update:endDate', value: Date | null): void;
  (e: 'change', value: { startDate: Date | null; endDate: Date | null }): void;
}>();

const showPicker = ref(props.autoOpen);
const reference = ref<HTMLElement | null>(null);
const pickerContainer = ref<HTMLElement | null>(null);
const floatingStyles = useDatepickerFloating(reference, pickerContainer);

const getCommittedDates = (): [Date | null, Date | null] | null =>
  props.startDate && props.endDate ? [props.startDate, props.endDate] : null;

// Draft shown in the input while picking; the filter only changes once the end is picked.
const internalDates = ref(getCommittedDates());
watch(getCommittedDates, (value) => (internalDates.value = value));

const applyInternalDates = () => {
  const [startDate, endDate] = internalDates.value ?? [];
  if (!startDate || !endDate) return false;
  if (
    startDate.getTime() !== props.startDate?.getTime() ||
    endDate.getTime() !== props.endDate?.getTime()
  ) {
    emit('update:startDate', startDate);
    emit('update:endDate', endDate);
    emit('change', { startDate, endDate });
  }
  return true;
};

const handleRangeSelected = () => {
  if (applyInternalDates()) showPicker.value = false;
};

onClickOutside(
  pickerContainer,
  () => {
    if (!showPicker.value) return;
    showPicker.value = false;
    // A new start can keep the previous end, so a complete draft is applied as shown in the input.
    if (!applyInternalDates()) internalDates.value = getCommittedDates();
  },
  { ignore: [reference] },
);

const hasValue = computed(() => props.startDate || props.endDate);

const displayValue = computed(() => {
  const [startDate, endDate] = internalDates.value ?? [];
  if (!startDate && !endDate) return '';
  const format = getDateFormat();
  const startStr = startDate ? formatDate(startDate, format) : '';
  const endStr = endDate ? formatDate(endDate, format) : '';
  if (startStr && endStr) return `${startStr} - ${endStr}`;
  if (startStr) return `${startStr} - ...`;
  return '';
});

const clearRange = () => {
  showPicker.value = false;
  internalDates.value = null;
  emit('update:startDate', null);
  emit('update:endDate', null);
  emit('change', { startDate: null, endDate: null });
};
</script>
